<script>
  import { onMount } from "svelte";
  import ContactForm from "./ContactForm.svelte";

  let navbarActive = false;
  let scrolled = false;
  let visibleSections = {};

  function toggleNavbar() {
    navbarActive = !navbarActive;
  }

  function closeNavbar() {
    navbarActive = false;
  }

  function scrollTo(id) {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    closeNavbar();
  }

  onMount(() => {
    const handleScroll = () => {
      scrolled = window.scrollY > 50;
    };
    window.addEventListener('scroll', handleScroll);

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          visibleSections[entry.target.id] = true;
          visibleSections = visibleSections;
        }
      });
    }, { threshold: 0.1 });

    document.querySelectorAll('.observe-section').forEach(el => observer.observe(el));

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  });
</script>

<!-- ═══════════ NAVBAR ═══════════ -->
<nav class="site-nav" class:scrolled aria-label="main navigation">
  <div class="nav-bar-bg"></div>
  <div class="nav-inner">
    <a class="nav-brand" href="/" on:click|preventDefault={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
      <span class="brand-mark">
        <span class="brand-mark-text">AI</span>
      </span>
      <span class="brand-name">Obfuscation<span class="brand-llc">LLC</span></span>
    </a>

    <button
      class="nav-toggle"
      class:is-open={navbarActive}
      aria-label="Toggle menu"
      aria-expanded={navbarActive}
      on:click={toggleNavbar}
    >
      <span></span>
      <span></span>
      <span></span>
    </button>

    <div class="nav-menu" class:is-open={navbarActive}>
      <a class="nav-link" href="#about" on:click|preventDefault={() => scrollTo('about')}>About</a>
      <a class="nav-link" href="#work" on:click|preventDefault={() => scrollTo('work')}>Our Work</a>
      <a class="nav-link" href="#process" on:click|preventDefault={() => scrollTo('process')}>Process</a>
      <a class="nav-link" href="#services" on:click|preventDefault={() => scrollTo('services')}>Services</a>
      <div class="nav-divider"></div>
      <a class="nav-cta" href="#contact" on:click|preventDefault={() => scrollTo('contact')}>
        Contact Us
        <i class="fas fa-arrow-right nav-cta-arrow"></i>
      </a>
    </div>
  </div>
</nav>

<!-- ═══════════ HERO ═══════════ -->
<section class="hero">
  <div class="hero-glow"></div>
  <div class="hero-grid-bg"></div>
  <div class="hero-content">
    <div class="hero-badge">
      <span class="hero-badge-dot"></span>
      AI Privacy & Obfuscation
    </div>
    <h1 class="hero-title">
      We build tools that keep<br />
      <span class="gradient-text">your content yours.</span>
    </h1>
    <p class="hero-desc">
      From AI detection evasion to community platforms, we ship real products
      that solve real problems. Small team. Big output. No BS.
    </p>
    <div class="hero-actions">
      <button class="btn-primary" on:click={() => scrollTo('contact')}>
        Get in Touch
        <i class="fas fa-arrow-right"></i>
      </button>
      <button class="btn-ghost" on:click={() => scrollTo('work')}>
        See Our Work
      </button>
    </div>
  </div>
  <div class="hero-fade"></div>
</section>

<!-- ═══════════ TRUST BAR ═══════════ -->
<section class="trust-bar observe-section" id="trust-bar" class:is-visible={visibleSections['trust-bar']}>
  <div class="container">
    <div class="trust-items">
      <div class="trust-item">
        <i class="fas fa-bolt"></i>
        <span>Fast Deployments</span>
      </div>
      <div class="trust-sep"></div>
      <div class="trust-item">
        <i class="fas fa-shield-alt"></i>
        <span>Privacy-First Architecture</span>
      </div>
      <div class="trust-sep"></div>
      <div class="trust-item">
        <i class="fas fa-code-branch"></i>
        <span>Open Source Roots</span>
      </div>
      <div class="trust-sep"></div>
      <div class="trust-item">
        <i class="fas fa-server"></i>
        <span>99.9% Uptime SLA</span>
      </div>
    </div>
  </div>
</section>

<!-- ═══════════ ABOUT ═══════════ -->
<section id="about" class="section observe-section" class:is-visible={visibleSections['about']}>
  <div class="container">
    <div class="section-header">
      <span class="section-label">Who We Are</span>
      <h2 class="section-heading">A small team building<br class="hide-mobile" /> tools nobody else will.</h2>
    </div>

    <div class="columns is-variable is-8 is-vcentered about-content">
      <div class="column is-7">
        <p class="body-text">
          We're engineers obsessed with the intersection of AI and privacy.
          In a world where models are trained on everything, we give
          people a say in how their content gets used.
        </p>
        <p class="body-text">
          No enterprise buzzwords. No bloated consulting decks. We ship products
          that real people use — fast, lean, and with a bias for action.
          Every tool starts from the same question: <em>"What would we want to exist?"</em>
        </p>
        <div class="about-highlights">
          <div class="highlight">
            <i class="fas fa-check"></i>
            <span>Fully bootstrapped — no VC, no strings</span>
          </div>
          <div class="highlight">
            <i class="fas fa-check"></i>
            <span>Engineering-led, product-driven culture</span>
          </div>
          <div class="highlight">
            <i class="fas fa-check"></i>
            <span>Multiple live products with real users</span>
          </div>
        </div>
      </div>
      <div class="column is-5">
        <div class="stats-grid">
          <div class="stat">
            <span class="stat-value">3+</span>
            <span class="stat-label">Products Shipped</span>
          </div>
          <div class="stat">
            <span class="stat-value">1M+</span>
            <span class="stat-label">Requests Handled</span>
          </div>
          <div class="stat">
            <span class="stat-value">24/7</span>
            <span class="stat-label">Monitored Uptime</span>
          </div>
          <div class="stat">
            <span class="stat-value">100%</span>
            <span class="stat-label">Bootstrapped</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- ═══════════ WHAT WE BUILD ═══════════ -->
<section id="work" class="section section-alt observe-section" class:is-visible={visibleSections['work']}>
  <div class="container">
    <div class="section-header centered">
      <span class="section-label">What We Build</span>
      <h2 class="section-heading">AI-powered platforms that<br class="hide-mobile" /> solve hard problems.</h2>
      <p class="section-desc">We don't build demos. We build products people depend on every day.</p>
    </div>

    <div class="card-grid">
      <div class="card">
        <div class="card-icon indigo">
          <i class="fas fa-shield-alt"></i>
        </div>
        <h3 class="card-title">AI Content Privacy</h3>
        <p class="card-text">
          Text obfuscation and cloaking tools that protect writing from AI detection.
          Transform content while keeping it readable and natural.
        </p>
        <div class="card-tags">
          <span class="tag">NLP</span>
          <span class="tag">Privacy</span>
          <span class="tag">Detection</span>
        </div>
      </div>

      <div class="card">
        <div class="card-icon violet">
          <i class="fas fa-robot"></i>
        </div>
        <h3 class="card-title">Detection Evasion</h3>
        <p class="card-text">
          Platforms that stay ahead of evolving AI classifiers. We study how
          detection works so we can build tools that beat it.
        </p>
        <div class="card-tags">
          <span class="tag">ML</span>
          <span class="tag">Adversarial</span>
          <span class="tag">Research</span>
        </div>
      </div>

      <div class="card">
        <div class="card-icon sky">
          <i class="fas fa-users"></i>
        </div>
        <h3 class="card-title">Community Platforms</h3>
        <p class="card-text">
          Social tools and community software built with modern stacks.
          Real-time features, clean UIs, and infrastructure that scales.
        </p>
        <div class="card-tags">
          <span class="tag">Real-time</span>
          <span class="tag">Social</span>
          <span class="tag">Scalable</span>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- ═══════════ PROCESS ═══════════ -->
<section id="process" class="section observe-section" class:is-visible={visibleSections['process']}>
  <div class="container">
    <div class="section-header centered">
      <span class="section-label">How We Work</span>
      <h2 class="section-heading">Idea to production,<br class="hide-mobile" /> without the runaround.</h2>
      <p class="section-desc">We move fast because we skip the parts that don't matter.</p>
    </div>

    <div class="process-grid">
      <div class="process-step">
        <div class="step-number">01</div>
        <div class="step-content">
          <h3 class="step-title">Understand</h3>
          <p class="step-text">We dig into the problem space, not just the feature request. What are you actually trying to solve? Who's it for? What exists already?</p>
        </div>
      </div>
      <div class="process-step">
        <div class="step-number">02</div>
        <div class="step-content">
          <h3 class="step-title">Prototype</h3>
          <p class="step-text">Working code within days, not weeks. We build the smallest version that proves the concept works — then validate before investing more.</p>
        </div>
      </div>
      <div class="process-step">
        <div class="step-number">03</div>
        <div class="step-content">
          <h3 class="step-title">Ship</h3>
          <p class="step-text">Production-ready deployment with monitoring, CI/CD, and infra that doesn't fall over. We don't hand off half-finished prototypes.</p>
        </div>
      </div>
      <div class="process-step">
        <div class="step-number">04</div>
        <div class="step-content">
          <h3 class="step-title">Iterate</h3>
          <p class="step-text">Real users generate real feedback. We watch the metrics, talk to users, and ship improvements weekly. The first launch is just the starting line.</p>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- ═══════════ SERVICES ═══════════ -->
<section id="services" class="section section-alt observe-section" class:is-visible={visibleSections['services']}>
  <div class="container">
    <div class="section-header centered">
      <span class="section-label">Services</span>
      <h2 class="section-heading">From idea to production.</h2>
      <p class="section-desc">We handle the hard parts so you can focus on the business.</p>
    </div>

    <div class="services-grid">
      <div class="service">
        <div class="service-icon"><i class="fas fa-code"></i></div>
        <div>
          <h3 class="service-title">Custom AI Development</h3>
          <p class="service-desc">Full-stack AI applications. We build the models, APIs, and interfaces — not just demos that break in production.</p>
        </div>
      </div>

      <div class="service">
        <div class="service-icon"><i class="fas fa-lock"></i></div>
        <div>
          <h3 class="service-title">Privacy & Obfuscation</h3>
          <p class="service-desc">Specialized tooling for content privacy, AI detection circumvention, and data obfuscation at scale.</p>
        </div>
      </div>

      <div class="service">
        <div class="service-icon"><i class="fas fa-lightbulb"></i></div>
        <div>
          <h3 class="service-title">Technical Consulting</h3>
          <p class="service-desc">Architecture decisions, scaling strategy, and AI implementation guidance from people who've actually built the thing.</p>
        </div>
      </div>

      <div class="service">
        <div class="service-icon"><i class="fas fa-rocket"></i></div>
        <div>
          <h3 class="service-title">Platform Engineering</h3>
          <p class="service-desc">End-to-end product dev — APIs, frontends, infra, CI/CD. Complete products, not prototypes that need another team to finish.</p>
        </div>
      </div>
    </div>

    <div class="tech-strip">
      <span class="tech-strip-label">Tech we use daily:</span>
      <div class="tech-tags">
        <span>Python</span>
        <span>TypeScript</span>
        <span>Svelte</span>
        <span>React</span>
        <span>Node.js</span>
        <span>PyTorch</span>
        <span>AWS</span>
        <span>Netlify</span>
        <span>PostgreSQL</span>
      </div>
    </div>
  </div>
</section>

<!-- ═══════════ CTA BANNER ═══════════ -->
<section class="cta-banner observe-section" id="cta-banner" class:is-visible={visibleSections['cta-banner']}>
  <div class="container">
    <div class="cta-inner">
      <div class="cta-glow"></div>
      <h2 class="cta-heading">Ready to build something that matters?</h2>
      <p class="cta-text">Whether you need a full product, a technical consult, or just want to brainstorm — we're here.</p>
      <button class="btn-primary btn-lg" on:click={() => scrollTo('contact')}>
        Start a Conversation
        <i class="fas fa-arrow-right"></i>
      </button>
    </div>
  </div>
</section>

<!-- ═══════════ CONTACT ═══════════ -->
<section id="contact" class="section observe-section" class:is-visible={visibleSections['contact']}>
  <div class="container">
    <div class="contact-wrap">
      <div class="section-header centered">
        <span class="section-label">Contact</span>
        <h2 class="section-heading">Let's talk.</h2>
        <p class="section-desc">Have a project in mind? Curious about what we can build? Drop us a line — we read everything.</p>
      </div>
      <ContactForm />
    </div>
  </div>
</section>

<!-- ═══════════ FOOTER ═══════════ -->
<footer class="footer">
  <div class="container">
    <div class="footer-grid">
      <div class="footer-col">
        <div class="footer-brand">
          <span class="brand-mark"><span class="brand-mark-text">AI</span></span>
          <span class="brand-name">Obfuscation<span class="brand-llc">LLC</span></span>
        </div>
        <p class="footer-text">AI tools that put privacy first. Built by engineers, for everyone.</p>
      </div>
      <div class="footer-col">
        <h4 class="footer-heading">Navigate</h4>
        <a href="#about" on:click|preventDefault={() => scrollTo('about')} class="footer-link">About</a>
        <a href="#work" on:click|preventDefault={() => scrollTo('work')} class="footer-link">Our Work</a>
        <a href="#process" on:click|preventDefault={() => scrollTo('process')} class="footer-link">Process</a>
        <a href="#services" on:click|preventDefault={() => scrollTo('services')} class="footer-link">Services</a>
        <a href="#contact" on:click|preventDefault={() => scrollTo('contact')} class="footer-link">Contact</a>
      </div>
      <div class="footer-col">
        <h4 class="footer-heading">Company</h4>
        <p class="footer-text">Artificial Intelligence Obfuscation LLC</p>
        <p class="footer-text footer-text-sm">Building from the intersection of AI and privacy.</p>
      </div>
    </div>
    <div class="footer-bottom">
      <span>&copy; {new Date().getFullYear()} Artificial Intelligence Obfuscation LLC. All rights reserved.</span>
    </div>
  </div>
</footer>

<style>
  /* ══════════════════════════════════
     NAVBAR
     ══════════════════════════════════ */
  .site-nav {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 100;
    padding: 0 2rem;
    transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .nav-bar-bg {
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(15, 23, 42, 0.95) 0%, rgba(15, 23, 42, 0.6) 70%, transparent 100%);
    pointer-events: none;
    transition: opacity 0.35s ease;
  }

  .site-nav.scrolled .nav-bar-bg {
    opacity: 0;
  }

  .site-nav.scrolled {
    background: rgba(22, 30, 50, 0.85);
    backdrop-filter: blur(20px) saturate(1.3);
    -webkit-backdrop-filter: blur(20px) saturate(1.3);
    box-shadow: 0 1px 0 rgba(148, 163, 184, 0.08), 0 4px 24px rgba(0, 0, 0, 0.2);
  }

  .nav-inner {
    position: relative;
    max-width: 1200px;
    margin: 0 auto;
    height: 68px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .nav-brand {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    text-decoration: none;
    cursor: pointer;
  }

  .brand-mark {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 9px;
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
    position: relative;
    overflow: hidden;
  }

  .brand-mark::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, transparent 40%, rgba(255,255,255,0.15));
    border-radius: 9px;
  }

  .brand-mark-text {
    font-weight: 800;
    font-size: 0.82rem;
    color: white;
    letter-spacing: -0.04em;
    position: relative;
    z-index: 1;
  }

  .brand-name {
    font-weight: 700;
    font-size: 1.1rem;
    color: var(--text-primary);
    letter-spacing: -0.025em;
  }

  .brand-llc {
    font-weight: 400;
    font-size: 0.7rem;
    color: var(--text-muted);
    margin-left: 0.3rem;
    letter-spacing: 0.04em;
    vertical-align: super;
  }

  .nav-menu {
    display: flex;
    align-items: center;
    gap: 0.15rem;
  }

  .nav-link {
    color: var(--text-muted);
    text-decoration: none;
    font-size: 0.88rem;
    font-weight: 500;
    padding: 0.45rem 0.85rem;
    border-radius: 7px;
    transition: all 0.15s ease;
    cursor: pointer;
  }

  .nav-link:hover {
    color: var(--text-primary);
    background: rgba(148, 163, 184, 0.1);
  }

  .nav-divider {
    width: 1px;
    height: 20px;
    background: var(--border);
    margin: 0 0.5rem;
  }

  .nav-cta {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    margin-left: 0.25rem;
    padding: 0.48rem 1.1rem;
    border-radius: 8px;
    background: linear-gradient(135deg, #6366f1, #7c3aed);
    color: white;
    font-size: 0.88rem;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.2s ease;
    cursor: pointer;
    box-shadow: 0 1px 4px rgba(99, 102, 241, 0.2);
  }

  .nav-cta:hover {
    background: linear-gradient(135deg, #818cf8, #8b5cf6);
    box-shadow: 0 2px 16px rgba(99, 102, 241, 0.35);
    transform: translateY(-1px);
  }

  .nav-cta-arrow {
    font-size: 0.7rem;
    transition: transform 0.2s ease;
  }

  .nav-cta:hover .nav-cta-arrow {
    transform: translateX(2px);
  }

  /* Mobile Toggle */
  .nav-toggle {
    display: none;
    flex-direction: column;
    justify-content: center;
    gap: 5px;
    width: 40px;
    height: 40px;
    padding: 8px;
    background: none;
    border: none;
    cursor: pointer;
    position: relative;
    z-index: 1;
  }

  .nav-toggle span {
    display: block;
    width: 100%;
    height: 2px;
    background: var(--text-secondary);
    border-radius: 2px;
    transition: all 0.3s ease;
  }

  .nav-toggle.is-open span:nth-child(1) {
    transform: rotate(45deg) translate(5px, 5px);
  }
  .nav-toggle.is-open span:nth-child(2) {
    opacity: 0;
  }
  .nav-toggle.is-open span:nth-child(3) {
    transform: rotate(-45deg) translate(5px, -5px);
  }

  /* ══════════════════════════════════
     HERO
     ══════════════════════════════════ */
  .hero {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    padding: 7rem 1.5rem 5rem;
    overflow: hidden;
  }

  .hero-glow {
    position: absolute;
    top: -10%;
    left: 50%;
    transform: translateX(-50%);
    width: 900px;
    height: 700px;
    background: radial-gradient(ellipse, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.05) 40%, transparent 70%);
    pointer-events: none;
  }

  .hero-grid-bg {
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(148, 163, 184, 0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(148, 163, 184, 0.03) 1px, transparent 1px);
    background-size: 60px 60px;
    mask-image: radial-gradient(ellipse at center, black 30%, transparent 70%);
    -webkit-mask-image: radial-gradient(ellipse at center, black 30%, transparent 70%);
    pointer-events: none;
  }

  .hero-fade {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 120px;
    background: linear-gradient(transparent, var(--bg-base));
    pointer-events: none;
  }

  .hero-content {
    position: relative;
    text-align: center;
    max-width: 720px;
    animation: fadeInUp 0.7s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .hero-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.4rem 1rem;
    border-radius: 50px;
    background: rgba(99, 102, 241, 0.08);
    border: 1px solid rgba(99, 102, 241, 0.18);
    color: var(--accent-bright);
    font-size: 0.82rem;
    font-weight: 600;
    letter-spacing: 0.01em;
    margin-bottom: 2rem;
  }

  .hero-badge-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #22c55e;
    box-shadow: 0 0 6px rgba(34, 197, 94, 0.4);
  }

  .hero-title {
    font-size: 4rem;
    font-weight: 800;
    line-height: 1.08;
    color: var(--text-primary);
    letter-spacing: -0.04em;
    margin-bottom: 1.5rem;
  }

  .hero-desc {
    font-size: 1.15rem;
    line-height: 1.7;
    color: var(--text-secondary);
    max-width: 540px;
    margin: 0 auto 2.5rem;
  }

  .hero-actions {
    display: flex;
    gap: 0.75rem;
    justify-content: center;
    flex-wrap: wrap;
  }

  /* ══════════════════════════════════
     BUTTONS
     ══════════════════════════════════ */
  .btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.75rem;
    border: none;
    border-radius: 8px;
    background: linear-gradient(135deg, #6366f1, #7c3aed);
    color: white;
    font-family: 'Inter', sans-serif;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 8px rgba(99, 102, 241, 0.2);
  }

  .btn-primary:hover {
    background: linear-gradient(135deg, #818cf8, #8b5cf6);
    transform: translateY(-1px);
    box-shadow: 0 4px 20px rgba(99, 102, 241, 0.35);
  }

  .btn-primary.btn-lg {
    padding: 0.9rem 2.25rem;
    font-size: 1rem;
  }

  .btn-primary i {
    font-size: 0.8rem;
    transition: transform 0.2s ease;
  }

  .btn-primary:hover i {
    transform: translateX(3px);
  }

  .btn-ghost {
    display: inline-flex;
    align-items: center;
    padding: 0.75rem 1.75rem;
    border: 1px solid var(--border-hover);
    border-radius: 8px;
    background: transparent;
    color: var(--text-secondary);
    font-family: 'Inter', sans-serif;
    font-size: 0.95rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn-ghost:hover {
    color: var(--text-primary);
    border-color: var(--text-muted);
    background: rgba(148, 163, 184, 0.05);
  }

  /* ══════════════════════════════════
     TRUST BAR
     ══════════════════════════════════ */
  .trust-bar {
    padding: 2.5rem 1.5rem;
    border-bottom: 1px solid var(--border);
  }

  .trust-items {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2rem;
    flex-wrap: wrap;
  }

  .trust-item {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    font-size: 0.88rem;
    font-weight: 500;
    color: var(--text-muted);
  }

  .trust-item i {
    color: var(--accent);
    font-size: 0.85rem;
  }

  .trust-sep {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: var(--border-hover);
  }

  /* ══════════════════════════════════
     SECTIONS COMMON
     ══════════════════════════════════ */
  .section {
    padding: 6.5rem 1.5rem;
  }

  .section-alt {
    background: rgba(30, 41, 59, 0.25);
  }

  .section-header {
    margin-bottom: 3.5rem;
  }

  .section-header.centered {
    text-align: center;
  }

  .section-label {
    display: inline-block;
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--accent);
    margin-bottom: 0.75rem;
  }

  .section-heading {
    font-size: 2.75rem;
    font-weight: 800;
    line-height: 1.1;
    letter-spacing: -0.035em;
    color: var(--text-primary);
  }

  .section-desc {
    margin-top: 0.75rem;
    font-size: 1.05rem;
    color: var(--text-secondary);
    line-height: 1.6;
    max-width: 480px;
  }

  .section-header.centered .section-desc {
    margin-left: auto;
    margin-right: auto;
  }

  /* ══════════════════════════════════
     ABOUT
     ══════════════════════════════════ */
  .body-text {
    font-size: 1.05rem;
    line-height: 1.8;
    color: var(--text-secondary);
    margin-bottom: 1rem;
  }

  .body-text em {
    color: var(--accent-bright);
    font-style: italic;
  }

  .about-content {
    margin-top: 0;
  }

  .about-highlights {
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
    margin-top: 1.5rem;
  }

  .highlight {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    font-size: 0.92rem;
    color: var(--text-secondary);
  }

  .highlight i {
    color: #22c55e;
    font-size: 0.75rem;
  }

  .stats-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1px;
    background: var(--border);
    border-radius: var(--radius-lg);
    overflow: hidden;
    border: 1px solid var(--border);
  }

  .stat {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 1.75rem 1rem;
    background: var(--bg-base);
    text-align: center;
    transition: background 0.2s ease;
  }

  .stat:hover {
    background: var(--bg-raised);
  }

  .stat-value {
    font-size: 1.75rem;
    font-weight: 800;
    color: var(--text-primary);
    letter-spacing: -0.03em;
    margin-bottom: 0.25rem;
  }

  .stat-label {
    font-size: 0.75rem;
    color: var(--text-muted);
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  /* ══════════════════════════════════
     CARDS
     ══════════════════════════════════ */
  .card-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.25rem;
  }

  .card {
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: var(--radius-lg);
    padding: 2rem;
    transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
    display: flex;
    flex-direction: column;
  }

  .card:hover {
    background: var(--bg-card-hover);
    border-color: var(--border-hover);
    transform: translateY(-3px);
    box-shadow: var(--shadow-lg);
  }

  .card-icon {
    width: 44px;
    height: 44px;
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
    margin-bottom: 1.25rem;
  }

  .card-icon.indigo {
    background: rgba(129, 140, 248, 0.12);
    color: #a5b4fc;
  }

  .card-icon.violet {
    background: rgba(192, 132, 252, 0.12);
    color: #d8b4fe;
  }

  .card-icon.sky {
    background: rgba(56, 189, 248, 0.12);
    color: #7dd3fc;
  }

  .card-title {
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 0.6rem;
    letter-spacing: -0.02em;
  }

  .card-text {
    font-size: 0.92rem;
    line-height: 1.65;
    color: var(--text-muted);
    flex: 1;
  }

  .card-tags {
    display: flex;
    gap: 0.4rem;
    margin-top: 1.25rem;
    flex-wrap: wrap;
  }

  .tag {
    font-size: 0.72rem;
    font-weight: 500;
    padding: 0.2rem 0.55rem;
    border-radius: 5px;
    background: rgba(148, 163, 184, 0.08);
    color: var(--text-muted);
    letter-spacing: 0.02em;
  }

  /* ══════════════════════════════════
     PROCESS
     ══════════════════════════════════ */
  .process-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.25rem;
  }

  .process-step {
    display: flex;
    gap: 1.25rem;
    padding: 2rem;
    border-radius: var(--radius-lg);
    border: 1px solid var(--border);
    background: var(--bg-card);
    transition: all 0.2s ease;
  }

  .process-step:hover {
    border-color: var(--border-hover);
    background: var(--bg-card-hover);
  }

  .step-number {
    font-size: 2rem;
    font-weight: 800;
    color: rgba(99, 102, 241, 0.2);
    letter-spacing: -0.04em;
    line-height: 1;
    min-width: 48px;
  }

  .step-title {
    font-size: 1.05rem;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 0.4rem;
    letter-spacing: -0.01em;
  }

  .step-text {
    font-size: 0.9rem;
    line-height: 1.65;
    color: var(--text-muted);
  }

  /* ══════════════════════════════════
     SERVICES
     ══════════════════════════════════ */
  .services-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }

  .service {
    display: flex;
    gap: 1rem;
    padding: 1.5rem;
    border-radius: var(--radius-md);
    border: 1px solid transparent;
    transition: all 0.2s ease;
  }

  .service:hover {
    background: rgba(148, 163, 184, 0.04);
    border-color: var(--border);
  }

  .service-icon {
    width: 40px;
    height: 40px;
    min-width: 40px;
    border-radius: var(--radius-sm);
    background: rgba(99, 102, 241, 0.08);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--accent);
    font-size: 1rem;
  }

  .service-title {
    font-size: 1rem;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 0.3rem;
    letter-spacing: -0.01em;
  }

  .service-desc {
    font-size: 0.9rem;
    line-height: 1.6;
    color: var(--text-muted);
  }

  .tech-strip {
    margin-top: 3rem;
    padding-top: 2rem;
    border-top: 1px solid var(--border);
    display: flex;
    align-items: center;
    gap: 1.25rem;
    flex-wrap: wrap;
  }

  .tech-strip-label {
    font-size: 0.82rem;
    font-weight: 600;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    white-space: nowrap;
  }

  .tech-tags {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .tech-tags span {
    font-size: 0.78rem;
    font-weight: 500;
    padding: 0.3rem 0.7rem;
    border-radius: 6px;
    background: rgba(99, 102, 241, 0.06);
    border: 1px solid rgba(99, 102, 241, 0.1);
    color: var(--accent-bright);
  }

  /* ══════════════════════════════════
     CTA BANNER
     ══════════════════════════════════ */
  .cta-banner {
    padding: 1.5rem 1.5rem;
  }

  .cta-inner {
    position: relative;
    max-width: 1200px;
    margin: 0 auto;
    text-align: center;
    padding: 5rem 2rem;
    border-radius: var(--radius-lg);
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.08), rgba(139, 92, 246, 0.08));
    border: 1px solid rgba(99, 102, 241, 0.12);
    overflow: hidden;
  }

  .cta-glow {
    position: absolute;
    top: -50%;
    left: 50%;
    transform: translateX(-50%);
    width: 500px;
    height: 400px;
    background: radial-gradient(ellipse, rgba(99, 102, 241, 0.12), transparent 70%);
    pointer-events: none;
  }

  .cta-heading {
    position: relative;
    font-size: 2.25rem;
    font-weight: 800;
    color: var(--text-primary);
    letter-spacing: -0.03em;
    margin-bottom: 0.75rem;
  }

  .cta-text {
    position: relative;
    font-size: 1.05rem;
    color: var(--text-secondary);
    margin-bottom: 2rem;
    max-width: 480px;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.6;
  }

  /* ══════════════════════════════════
     CONTACT
     ══════════════════════════════════ */
  .contact-wrap {
    max-width: 520px;
    margin: 0 auto;
  }

  /* ══════════════════════════════════
     FOOTER
     ══════════════════════════════════ */
  .footer {
    padding: 4rem 1.5rem 2rem;
    background: var(--bg-raised);
    border-top: 1px solid var(--border);
  }

  .footer-grid {
    display: grid;
    grid-template-columns: 2fr 1fr 1.5fr;
    gap: 3rem;
    max-width: 1200px;
    margin: 0 auto;
  }

  .footer-brand {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
  }

  .footer-brand .brand-mark {
    width: 28px;
    height: 28px;
    border-radius: 7px;
  }

  .footer-brand .brand-mark-text {
    font-size: 0.7rem;
  }

  .footer-brand .brand-name {
    font-size: 1rem;
  }

  .footer-heading {
    font-size: 0.75rem;
    font-weight: 700;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: 1rem;
  }

  .footer-text {
    font-size: 0.9rem;
    color: var(--text-muted);
    line-height: 1.6;
  }

  .footer-text-sm {
    font-size: 0.82rem;
    margin-top: 0.4rem;
  }

  .footer-link {
    display: block;
    color: var(--text-muted);
    text-decoration: none;
    font-size: 0.9rem;
    padding: 0.25rem 0;
    transition: color 0.15s ease;
    cursor: pointer;
  }

  .footer-link:hover {
    color: var(--text-primary);
  }

  .footer-col {
    display: flex;
    flex-direction: column;
  }

  .footer-bottom {
    max-width: 1200px;
    margin: 3rem auto 0;
    padding-top: 1.5rem;
    border-top: 1px solid var(--border);
    text-align: center;
    font-size: 0.82rem;
    color: var(--text-muted);
  }

  /* ══════════════════════════════════
     RESPONSIVE
     ══════════════════════════════════ */
  @media (max-width: 1024px) {
    .nav-toggle {
      display: flex;
    }

    .nav-menu {
      display: none;
      position: absolute;
      top: 68px;
      left: 0;
      right: 0;
      flex-direction: column;
      padding: 1rem 1.5rem 1.5rem;
      background: rgba(15, 23, 42, 0.97);
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
      border-bottom: 1px solid var(--border);
      gap: 0.25rem;
    }

    .nav-menu.is-open {
      display: flex;
    }

    .nav-divider {
      display: none;
    }

    .nav-cta {
      margin-left: 0;
      margin-top: 0.5rem;
      text-align: center;
      justify-content: center;
    }
  }

  @media (max-width: 768px) {
    .site-nav {
      padding: 0 1rem;
    }

    .hero-title {
      font-size: 2.5rem;
    }

    .hero-desc {
      font-size: 1.05rem;
    }

    .section {
      padding: 4.5rem 1.25rem;
    }

    .section-heading {
      font-size: 2rem;
    }

    .card-grid {
      grid-template-columns: 1fr;
      gap: 1rem;
    }

    .process-grid {
      grid-template-columns: 1fr;
    }

    .services-grid {
      grid-template-columns: 1fr;
    }

    .hero-actions {
      flex-direction: column;
      align-items: center;
    }

    .btn-primary, .btn-ghost {
      width: 100%;
      max-width: 280px;
      justify-content: center;
    }

    .footer-grid {
      grid-template-columns: 1fr;
      gap: 2rem;
    }

    .hide-mobile {
      display: none;
    }

    .stats-grid {
      margin-top: 2rem;
    }

    .trust-items {
      gap: 1rem;
    }

    .trust-sep {
      display: none;
    }

    .trust-item {
      font-size: 0.82rem;
    }

    .cta-heading {
      font-size: 1.65rem;
    }

    .cta-inner {
      padding: 3.5rem 1.5rem;
    }

    .tech-strip {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  @media (max-width: 480px) {
    .hero-title {
      font-size: 2rem;
    }

    .section-heading {
      font-size: 1.65rem;
    }

    .trust-items {
      flex-direction: column;
      gap: 0.75rem;
    }
  }
</style>
